import api from './api.service';

type ApiBody<T> = { success?: boolean; data?: T; message?: string };

async function unwrap<T>(promise: Promise<unknown>): Promise<T> {
  const body = (await promise) as ApiBody<T>;
  if (body?.data === undefined) {
    throw new Error(body?.message || 'Strategy request failed');
  }
  return body.data;
}

export const strategyService = {
  getDashboard: () => unwrap<Record<string, unknown>>(api.get('/strategy/dashboard')),
  getRuntime: () => unwrap<Record<string, unknown>>(api.get('/strategy/runtime')),
  getContext: () => unwrap<Record<string, unknown>>(api.get('/strategy/context')),
  listObjectives: () => unwrap<unknown[]>(api.get('/strategy/objectives')),
  createObjective: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/objectives', payload)),
  patchObjective: (id: string, payload: Record<string, unknown>) =>
    unwrap<unknown>(api.patch(`/strategy/objectives/${id}`, payload)),
  listKeyResults: (objectiveId?: string) =>
    unwrap<unknown[]>(
      api.get('/strategy/key-results', { params: objectiveId ? { objectiveId } : undefined })
    ),
  createKeyResult: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/key-results', payload)),
  getScorecard: () => unwrap<Record<string, unknown>>(api.get('/strategy/scorecard')),
  listKpis: () => unwrap<unknown[]>(api.get('/strategy/kpis')),
  getPortfolio: () => unwrap<Record<string, unknown>>(api.get('/strategy/portfolio')),
  listInvestments: () => unwrap<unknown[]>(api.get('/strategy/investments')),
  createInvestment: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/investments', payload)),
  runSimulation: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/simulations', payload)),
  listSimulations: () => unwrap<unknown[]>(api.get('/strategy/simulations')),
  getBoardPack: (generate?: boolean) =>
    unwrap<Record<string, unknown>>(
      api.get('/strategy/board-pack', { params: generate ? { generate: true } : undefined })
    ),
  getBoardWorkspace: () => unwrap<Record<string, unknown>>(api.get('/strategy/board')),
  createMeeting: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/board/meetings', payload)),
  recordDecision: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/board/decisions', payload)),
  getBrief: (kind?: string, question?: string) =>
    unwrap<Record<string, unknown>>(
      api.get('/strategy/brief', {
        params: {
          ...(kind ? { kind } : {}),
          ...(question ? { question } : {}),
        },
      })
    ),
  listRisks: () => unwrap<unknown[]>(api.get('/strategy/risks')),
  getHeatmap: () => unwrap<Record<string, unknown>>(api.get('/strategy/heatmap')),
  getBenchmark: () => unwrap<Record<string, unknown>>(api.get('/strategy/benchmark')),
  assessCapacity: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/capacity', payload)),
  createValuation: (payload: Record<string, unknown>) =>
    unwrap<unknown>(api.post('/strategy/valuations', payload)),
};
